import { supabase } from '@/integrations/supabase/client';
import type {
  CasoBuscaAtiva,
  PresencaBuscaAtiva,
  BuscaAtivaStatus,
  NivelRisco,
} from '../types/buscaAtiva.types';

const casosTable = () => supabase.from('casos_busca_ativa' as never) as any;

export const buscaAtivaService = {
  async getCasos(escolaId: string, alunoIds?: string[]): Promise<Map<string, CasoBuscaAtiva>> {
    let query = casosTable()
      .select('*')
      .eq('escola_id', escolaId);

    if (alunoIds) {
      if (!alunoIds.length) return new Map();
      query = query.in('aluno_id', alunoIds);
    }

    const { data, error } = await query;

    if (error) {
      console.warn('Casos de Busca Ativa indisponíveis. A migration pode não ter sido aplicada.', error);
      return new Map();
    }

    return new Map(
      ((data || []) as CasoBuscaAtiva[]).map(caso => [
        caso.aluno_id,
        {
          ...caso,
          motivos_risco: Array.isArray(caso.motivos_risco) ? caso.motivos_risco : [],
        },
      ]),
    );
  },

  async ensureCasos(
    escolaId: string,
    casos: Array<{
      alunoId: string;
      score: number;
      nivel: NivelRisco;
      motivos: string[];
    }>,
  ): Promise<void> {
    if (!casos.length) return;

    const rows = casos.map(caso => ({
      escola_id: escolaId,
      aluno_id: caso.alunoId,
      status: 'NOVO_ALERTA' satisfies BuscaAtivaStatus,
      score_risco: caso.score,
      nivel_risco: caso.nivel,
      motivos_risco: caso.motivos,
    }));

    const { error } = await casosTable().upsert(rows, {
      onConflict: 'escola_id,aluno_id',
      ignoreDuplicates: true,
    });

    if (error) {
      console.warn('Não foi possível inicializar os casos de Busca Ativa.', error);
    }
  },

  async registrarAcao(params: {
    escolaId: string;
    alunoId: string;
    formaContato: string;
    anotacao: string;
    status: BuscaAtivaStatus;
    monitorResponsavel: string;
    caso: CasoBuscaAtiva;
  }): Promise<CasoBuscaAtiva> {
    const { caso } = params;
    const { data, error } = await (supabase.rpc as any)('registrar_acao_busca_ativa', {
      p_escola_id: params.escolaId,
      p_aluno_id: params.alunoId,
      p_forma_contato: params.formaContato,
      p_anotacao: params.anotacao,
      p_status: params.status,
      p_monitor_responsavel: params.monitorResponsavel,
      p_barreira_principal: caso.barreira_principal || null,
      p_responsavel_user_id: caso.responsavel_user_id || null,
      p_responsavel_nome: caso.responsavel_nome || null,
      p_proxima_acao: caso.proxima_acao || null,
      p_proximo_follow_up: caso.proximo_follow_up || null,
      p_primeiro_contato_em: caso.primeiro_contato_em || null,
      p_data_prevista_retorno: caso.data_prevista_retorno || null,
      p_retornou_em: caso.retornou_em || null,
      p_recuperado_em: caso.recuperado_em || null,
      p_fechado_em: caso.fechado_em || null,
      p_motivo_fechamento: caso.motivo_fechamento || null,
      p_nivel_risco: caso.nivel_risco || null,
      p_score_risco: caso.score_risco ?? null,
      p_motivos_risco: caso.motivos_risco || [],
    });

    if (error) throw error;
    return data as CasoBuscaAtiva;
  },

  async getPresencasRecentes(
    escolaId: string,
    alunoIds: string[],
  ): Promise<Map<string, PresencaBuscaAtiva[]>> {
    if (!alunoIds.length) return new Map();

    const inicio = new Date();
    inicio.setDate(inicio.getDate() - 30);

    const { data, error } = await supabase
      .from('presencas')
      .select('aluno_id, data_chamada, presente, falta_justificada')
      .eq('escola_id', escolaId)
      .in('aluno_id', alunoIds)
      .gte('data_chamada', inicio.toISOString().slice(0, 10))
      .order('data_chamada', { ascending: false });

    if (error) throw error;

    const map = new Map<string, PresencaBuscaAtiva[]>();
    (data || []).forEach(item => {
      const lista = map.get(item.aluno_id) || [];
      lista.push({
        data_chamada: item.data_chamada,
        presente: item.presente,
        falta_justificada: item.falta_justificada,
      });
      map.set(item.aluno_id, lista);
    });
    return map;
  },
};
